import { Button, IconButton, MenuItem, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';

import { ICalendarViewValue } from '@customTypes/calendar';
import Iconify from 'components/Iconify';
import MenuPopover from 'components/MenuPopover';
import useLocales from 'hooks/useLocales';
import useResponsive from 'hooks/useResponsive';
import { fDate } from 'utils/formatTime';

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

type Props = {
    date: Date;
    view: ICalendarViewValue;
    onToday: VoidFunction;
    onNextDate: VoidFunction;
    onPrevDate: VoidFunction;
    onChangeView: (newView: ICalendarViewValue) => void;
};

export default function CalendarToolbar({ date, view, onToday, onNextDate, onPrevDate, onChangeView }: Props) {
    const { translate, currentLang } = useLocales();
    const isDesktop = useResponsive('up', 'sm');

    const VIEW_OPTIONS = [
        { value: 'dayGridMonth', label: translate('month'), icon: 'ic:round-view-module' },
        { value: 'timeGridWeek', label: translate('week'), icon: 'ic:round-view-week' },
        { value: 'timeGridDay', label: translate('day'), icon: 'ic:round-view-day' },
        { value: 'listWeek', label: translate('agenda'), icon: 'ic:round-view-agenda' },
    ] as const;

    const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);

    const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
        setOpenPopover(event.currentTarget);
    };

    const handleClosePopover = () => {
        setOpenPopover(null);
    };

    const selectedItem = VIEW_OPTIONS.filter(item => item.value === view)[0];

    return (
        <>
            <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ p: 2.5, pr: 2 }}>
                {isDesktop && (
                    <Button
                        color='inherit'
                        onClick={handleOpenPopover}
                        startIcon={<Iconify icon={selectedItem.icon} />}
                        endIcon={<Iconify icon='eva:chevron-down-fill' />}
                        sx={{
                            py: 0.5,
                            pl: 1.5,
                            bgcolor: 'action.selected',
                            '& .MuiButton-endIcon': { ml: 0.5 },
                        }}
                    >
                        {selectedItem.label}
                    </Button>
                )}

                <Stack direction='row' alignItems='center' spacing={1}>
                    <IconButton onClick={onPrevDate}>
                        <Iconify icon='eva:arrow-ios-back-fill' />
                    </IconButton>

                    <Typography variant='h6' sx={{ textTransform: 'capitalize' }}>
                        {fDate(
                            date,
                            currentLang.value,
                            view === 'dayGridMonth' || view === 'timeGridWeek' ? 'LLLL' : 'yyyy-MM-dd',
                        )}
                    </Typography>

                    <IconButton onClick={onNextDate}>
                        <Iconify icon='eva:arrow-ios-forward-fill' />
                    </IconButton>
                </Stack>

                <Stack direction='row' alignItems='center' spacing={1}>
                    <Button size='small' color='secondary' variant='contained' onClick={onToday}>
                        {translate('today')}
                    </Button>
                </Stack>
            </Stack>

            <MenuPopover open={openPopover} onClose={handleClosePopover} arrow='top-left' sx={{ width: 160 }}>
                {VIEW_OPTIONS.map(viewOption => (
                    <MenuItem
                        key={viewOption.value}
                        onClick={() => {
                            handleClosePopover();
                            onChangeView(viewOption.value);
                        }}
                        sx={{
                            ...(viewOption.value === view && {
                                bgcolor: 'action.selected',
                            }),
                        }}
                    >
                        <Iconify icon={viewOption.icon} />
                        {viewOption.label}
                    </MenuItem>
                ))}
            </MenuPopover>
        </>
    );
}
